import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Phone, Globe, MapPin, ChevronRight, Loader2, ShieldCheck } from 'lucide-react';
import Seo from '../../components/Seo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResourceStateLanding = () => {
  const { seg1: categorySlug, seg2: stateSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .get(`${API_URL}/community-resources/seo/${categorySlug}/${stateSlug}`)
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [categorySlug, stateSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center" data-testid="cr-state-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Seo title="Resource Guide Not Found" noindex path={`/community-resources/${categorySlug}/${stateSlug}`} />
          <h1 className="font-serif text-3xl font-bold text-gray-900">Resource guide not found</h1>
          <p className="mt-3 text-gray-600">This community resource guide isn’t available.</p>
          <Link to="/community/resources" className="mt-6 inline-block text-primary font-semibold">← Back to Community Resources</Link>
        </div>
      </div>
    );
  }

  const { content, resources = [], indexable, canonical_path, related_states = [], related_categories = [], state, category, last_updated } = data;
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://ineedasocialworker.com';
  const c = content || {};

  const breadcrumb = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: 'Community Resources', item: `${origin}/community/resources` },
      { '@type': 'ListItem', position: 3, name: category.label, item: `${origin}/community-resources/${category.slug}` },
      { '@type': 'ListItem', position: 4, name: `${category.label} in ${state.name}`, item: `${origin}${canonical_path}` },
    ],
  };
  const faqSchema = (c.faqs && c.faqs.length) ? {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: c.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  } : null;
  const itemList = (resources.length) ? {
    '@context': 'https://schema.org', '@type': 'ItemList',
    itemListElement: resources.slice(0, 25).map((r, i) => ({
      '@type': 'ListItem', position: i + 1, name: r.name, ...(r.website ? { url: r.website } : {}),
    })),
  } : null;

  const updated = last_updated ? new Date(last_updated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

  return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid="cr-state-landing">
      <Seo
        exactTitle={c.title ? `${c.title}` : `${category.label} in ${state.name} | I Need A Social Worker`}
        description={c.meta_description}
        path={canonical_path}
        type="article"
        noindex={!indexable}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
        {itemList && <script type="application/ld+json">{JSON.stringify(itemList)}</script>}
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center flex-wrap gap-1 text-sm text-gray-500 mb-6" data-testid="cr-breadcrumb">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/community/resources" className="hover:text-primary">Community Resources</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to={`/community-resources/${category.slug}`} className="hover:text-primary">{category.label}</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-gray-900 font-medium">{state.name}</span>
        </nav>

        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-gray-900" data-testid="cr-state-h1">
          {c.h1 || `${category.label} Resources in ${state.name}`}
        </h1>
        {updated && <p className="mt-2 text-sm text-gray-500">Last updated {updated}</p>}

        {c.intro_html
          ? <div className="prose prose-gray max-w-none mt-5 text-gray-700" dangerouslySetInnerHTML={{ __html: c.intro_html }} />
          : <p className="mt-5 text-gray-700">We’re preparing a detailed {category.label.toLowerCase()} guide for {state.name}. In the meantime, dial <strong>2-1-1</strong> or browse the verified resources below.</p>}

        {/* Key programs */}
        {c.key_programs && c.key_programs.length > 0 && (
          <div className="mt-8" data-testid="cr-key-programs">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Key programs in {state.name}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {c.key_programs.map((p, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-gray-100" data-testid="cr-program-card">
                  <h3 className="font-semibold text-gray-900">{p.name}</h3>
                  {p.what && <p className="text-sm text-gray-600 mt-1">{p.what}</p>}
                  {p.how_to_access && <p className="text-sm text-gray-500 mt-2"><span className="font-medium text-gray-700">How to access:</span> {p.how_to_access}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live resources */}
        <div className="mt-10" data-testid="cr-resource-list">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {resources.length > 0 ? `${category.label} resources for ${state.name}` : `More ${category.label.toLowerCase()} help`}
          </h2>
          {resources.length === 0 && (
            <p className="text-gray-600">Call <strong>2-1-1</strong> for local {category.label.toLowerCase()} referrals in {state.name}.</p>
          )}
          <div className="space-y-3">
            {resources.map((r, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-testid="cr-resource-card">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{r.name}</h3>
                    {r.is_national && <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"><ShieldCheck className="w-3 h-3" /> Nationwide</span>}
                  </div>
                  {r.description && <p className="text-sm text-gray-600 mt-1 line-clamp-3">{r.description}</p>}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                    {(r.city || r.state) && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{[r.city, r.state].filter(Boolean).join(', ')}</span>}
                    {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone className="w-3.5 h-3.5" />{r.phone}</a>}
                  </div>
                </div>
                {r.website && (
                  <a href={r.website} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-primary" data-testid="cr-resource-website">
                    <Globe className="w-4 h-4" /> Visit
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Internal links: other categories in this state */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          <div data-testid="cr-related-categories">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Other resources in {state.name}</h2>
            <ul className="space-y-1.5">
              {related_categories.map((rc) => (
                <li key={rc.slug}>
                  <Link to={`/community-resources/${rc.slug}/${state.slug}`} className="text-primary hover:underline text-sm">{rc.label} in {state.name}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="cr-related-states">
            <h2 className="text-base font-semibold text-gray-900 mb-3">{category.label} in other states</h2>
            <div className="flex flex-wrap gap-x-3 gap-y-1.5">
              {related_states.map((rs) => (
                <Link key={rs.slug} to={`/community-resources/${category.slug}/${rs.slug}`} className="text-sm text-gray-600 hover:text-primary">{rs.name}</Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10">
          <Link to="/community/resources" className="text-primary font-semibold" data-testid="cr-back-directory">← Back to the full Community Resource Directory</Link>
        </div>
      </div>
    </div>
  );
};

export default ResourceStateLanding;
